import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from '../../services/api';
import therapeuticAreaConfig from '../../config/therapeuticAreaConfig.json';
import './GuideForm.css';

// Error Icon Component
const ErrorIcon = () => (
  <span className="error-icon-wrapper" data-tooltip="This field is required">
    <svg className="error-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7" stroke="#DC2626" strokeWidth="2" fill="none"/>
      <path d="M8 4V9" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="8" cy="11.5" r="0.5" fill="#DC2626"/>
    </svg>
  </span>
);

// Custom Dropdown Component
const CustomDropdown = ({ id, value, options, onChange, onBlur, disabled, hasError }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrollThumbHeight, setScrollThumbHeight] = useState(0);
  const [scrollThumbTop, setScrollThumbTop] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [needsScroll, setNeedsScroll] = useState(false);
  const dropdownRef = useRef(null);
  const menuInnerRef = useRef(null);
  const scrollbarRef = useRef(null);
  const dragStartY = useRef(0);
  const dragStartScrollTop = useRef(0);

  const selectedOption = options.find(opt => opt.value === value) || options[0];
  // Filter out the selected option from the dropdown list
  const availableOptions = options.filter(opt => opt.value !== value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && menuInnerRef.current) {
      const el = menuInnerRef.current;
      const trackHeight = el.clientHeight;
      const contentHeight = el.scrollHeight;

      // Check if content overflows (needs scrolling)
      const hasOverflow = contentHeight > trackHeight;
      setNeedsScroll(hasOverflow);

      if (hasOverflow) {
        // Calculate thumb height proportional to visible/total content
        const ratio = trackHeight / contentHeight;
        const thumbHeight = Math.max(ratio * trackHeight, 20); // min 20px for usability
        setScrollThumbHeight(thumbHeight);
        setScrollThumbTop(0);
      }
    }
  }, [isOpen, availableOptions.length]);

  const handleScroll = () => {
    if (menuInnerRef.current && !isDragging) {
      const el = menuInnerRef.current;
      const trackHeight = el.clientHeight;
      const contentHeight = el.scrollHeight;
      const ratio = el.clientHeight / contentHeight;
      const thumbHeight = Math.max(ratio * trackHeight, 30);
      const scrollRatio = el.scrollTop / (contentHeight - el.clientHeight);
      const maxTop = trackHeight - thumbHeight;
      setScrollThumbTop(scrollRatio * maxTop);
    }
  };

  // Handle scrollbar thumb drag
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartY.current = e.clientY;
    dragStartScrollTop.current = menuInnerRef.current?.scrollTop || 0;
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !menuInnerRef.current) return;

      const el = menuInnerRef.current;
      const trackHeight = el.clientHeight;
      const contentHeight = el.scrollHeight;
      const thumbHeight = Math.max((el.clientHeight / contentHeight) * trackHeight, 30);
      const maxTop = trackHeight - thumbHeight;
      const maxScroll = contentHeight - el.clientHeight;

      const deltaY = e.clientY - dragStartY.current;
      const scrollDelta = (deltaY / maxTop) * maxScroll;

      el.scrollTop = dragStartScrollTop.current + scrollDelta;

      // Update thumb position
      const scrollRatio = el.scrollTop / maxScroll;
      setScrollThumbTop(scrollRatio * maxTop);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Handle click on scrollbar track to jump to position
  const handleTrackClick = (e) => {
    if (!menuInnerRef.current || !scrollbarRef.current) return;
    if (e.target.classList.contains('dropdown-scrollbar-thumb')) return;

    const el = menuInnerRef.current;
    const trackRect = scrollbarRef.current.getBoundingClientRect();
    const clickY = e.clientY - trackRect.top;
    const trackHeight = el.clientHeight;
    const contentHeight = el.scrollHeight;
    const maxScroll = contentHeight - el.clientHeight;

    const scrollRatio = clickY / trackHeight;
    el.scrollTop = scrollRatio * maxScroll;
  };

  // Handle mouse wheel scroll
  const handleWheel = (e) => {
    if (!menuInnerRef.current) return;
    e.preventDefault();
    const el = menuInnerRef.current;
    el.scrollTop += e.deltaY;
  };

  const handleSelect = (optionValue) => {
    onChange({ target: { value: optionValue } });
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      className={`custom-dropdown ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''} ${hasError ? 'error' : ''}`}
      id={id}
    >
      <button
        type="button"
        className="dropdown-trigger"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onBlur={onBlur}
        disabled={disabled}
      >
        <span className="dropdown-value">{selectedOption?.label}</span>
        <svg className="dropdown-arrow" width="14" height="7" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1.5L6 6.5L11 1.5" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {isOpen && (
        <div className="dropdown-menu" onWheel={handleWheel}>
          <div className="dropdown-menu-inner" ref={menuInnerRef} onScroll={handleScroll}>
            {availableOptions.map((option) => (
              <div
                key={option.value}
                className="dropdown-option"
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
          {needsScroll && (
            <div
              className="dropdown-scrollbar"
              ref={scrollbarRef}
              onClick={handleTrackClick}
            >
              <div
                className="dropdown-scrollbar-thumb"
                style={{ height: `${scrollThumbHeight}px`, top: `${scrollThumbTop}px` }}
                onMouseDown={handleThumbMouseDown}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// URL validation helper
const isValidURL = (string) => {
  if (!string || string.trim() === '') return true; // Empty is valid (not required)
  try {
    const url = new URL(string);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

const quillModulesMinimal = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ script: 'sub' }, { script: 'super' }]
    ],
};

const quillFormatsMinimal = ['bold', 'italic', 'underline', 'script'];

const getPlainTextLength = (html = '') => {
  if (!html) return 0;
  // Create temp element to get text content (same as Quill's getText)
  const temp = document.createElement('div');
  temp.innerHTML = html;
  const text = temp.textContent || temp.innerText || '';
  // Remove trailing newline like Quill adds
  return text.replace(/\n$/, '').length;
};

// Limited Quill component that enforces character limits
const LimitedQuill = ({ value, onChange, maxLength, onBlur, modules, formats, placeholder, readOnly }) => {
  const quillRef = useRef(null);

  useEffect(() => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    if (!editor) return;

    // Handle text-change to enforce limit
    const handleTextChange = (_delta, _oldDelta, source) => {
      if (source !== 'user') return;

      // Get current length (subtract 1 for Quill's trailing newline)
      const currentLength = editor.getLength() - 1;

      if (currentLength > maxLength) {
        // Delete excess characters from the end
        editor.deleteText(maxLength, currentLength - maxLength);
      }
    };

    editor.on('text-change', handleTextChange);
    return () => {
      editor.off('text-change', handleTextChange);
    };
  }, [maxLength]);

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      value={value || ''}
      onChange={onChange}
      onBlur={onBlur}
      modules={modules}
      formats={formats}
      placeholder={placeholder}
      readOnly={readOnly}
    />
  );
};

const GuideForm = ({ guide, onUpdate, onSave, onBlur, isSaving, isPublished, token, jwtToken, guideId, user, isGuestUser, hasAttemptedSave, onSaveAttempt }) => {
  const [isUploadingAuthorImage, setIsUploadingAuthorImage] = useState(false);
  const authorImageInputRef = useRef(null);

  const therapeuticAreas = [
    { value: 'hematology', label: 'Hematology' },
    { value: 'oncology', label: 'Oncology' },
    { value: 'neurology', label: 'Neurology' },
    { value: 'ophthalmology', label: 'Ophthalmology' },
    { value: 'pan-therapeutic', label: 'Pan-Therapeutic' },
  ];

  // Get header backgrounds based on selected therapeutic area
  const headerBackgrounds = useMemo(() => {
    const selectedArea = guide.therapeutic_area;
    if (selectedArea && therapeuticAreaConfig[selectedArea]) {
      return therapeuticAreaConfig[selectedArea].headerBackgrounds;
    }
    // Default fallback if no therapeutic area is selected
    return [
      { id: 1, name: 'GNE_Ophthalmology_Topic_Header', image: '/images/headers/GNE_Ophthalmology_Topic_Header.png' },
      { id: 2, name: 'GNE_Mouse_Eye_Blue_Green', image: '/images/headers/GNE_Mouse_Eye_Blue_Green.png' },
      { id: 3, name: 'GNE_Retina_Blue', image: '/images/headers/GNE_Retina_Blue.png' },
    ];
  }, [guide.therapeutic_area]);

  // Disable editing if the guide is published
  const isDisabled = isPublished;

  const [isOpen, setIsOpen] = useState(false);
  const [urlErrors, setUrlErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  // Helper function to validate guide and return errors
  const validateGuide = useCallback(() => {
    const requiredFields = [
      { field: 'therapeutic_area', label: 'Therapeutic Area' },
      { field: 'title', label: 'Quick Guide Title', isQuill: true },
      { field: 'event_location', label: 'Event Name & Location', isQuill: true },
      { field: 'disclaimers', label: 'Disclaimers', isQuill: true },
    ];

    const errors = {};
    for (const { field, isQuill } of requiredFields) {
      const value = guide[field];

      if (isQuill) {
        if (!value || isQuillEmpty(value)) {
          errors[field] = true;
        }
      } else {
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          errors[field] = true;
        }
      }
    }
    return errors;
  }, [guide]);

  // Validate and show errors when reopening accordion if user has previously attempted to save
  // Also validate URL fields on mount to persist URL errors when accordion reopens
  useEffect(() => {
    // Always check URL fields for invalid values (these should persist across accordion open/close)
    const urlFieldsToCheck = ['medically_url', 'society1_url', 'society2_url'];
    const newUrlErrors = {};

    for (const field of urlFieldsToCheck) {
      const value = guide[field];
      if (value && typeof value === 'string' && value.trim() !== '' && !isValidURL(value)) {
        newUrlErrors[field] = true;
      }
    }

    // If user has attempted save, run full validation
    if (hasAttemptedSave) {
      const errors = validateGuide();
      setFieldErrors(errors);
      // Also set URL errors from validation
      setUrlErrors(prev => ({ ...prev, ...newUrlErrors }));
    } else if (Object.keys(newUrlErrors).length > 0) {
      // Otherwise, just set URL errors if any
      setUrlErrors(prev => ({ ...prev, ...newUrlErrors }));
    }
  }, [hasAttemptedSave, validateGuide, guide.medically_url, guide.society1_url, guide.society2_url]); // Run when hasAttemptedSave changes or guide data changes

  // Clear field error when user enters a valid value
  const clearFieldError = (field) => {
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  // Clear URL error when user enters a valid URL
  const clearUrlError = (field) => {
    if (urlErrors[field]) {
      setUrlErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  // Handle URL field blur - validate URL format
  const handleUrlBlur = (field, value) => {
    if (value && !isValidURL(value)) {
      setUrlErrors(prev => ({ ...prev, [field]: true }));
    } else {
      setUrlErrors(prev => ({ ...prev, [field]: false }));
    }
    onBlur && onBlur();
  };

  // Handle Author Panel Image upload
  const handleAuthorImageChange = async (e) => {
    const file = e.target.files[0];
    if (file && token && guideId) {
      setIsUploadingAuthorImage(true);
      try {
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Image = reader.result;
          try {
            // Upload image to server
            const response = await api.uploadThumbnail(token, jwtToken, guideId, base64Image, user, isGuestUser, true);
            if (response && response.thumbnail_url) {
              onUpdate('author_panel_image', response.thumbnail_url);
            } else {
              // Fallback to base64 if URL not returned
              onUpdate('author_panel_image', base64Image);
            }
          } catch (uploadError) {
            console.error('Error uploading author image:', uploadError);
            alert('Failed to upload image. Please try again.');
          } finally {
            setIsUploadingAuthorImage(false);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error reading file:', error);
        setIsUploadingAuthorImage(false);
      }
    } else if (file) {
      // If no token/guideId, just use base64 (for new unsaved guides)
      const reader = new FileReader();
      reader.onloadend = () => {
        onUpdate('author_panel_image', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Check if a Quill field is empty
  const isQuillEmpty = (html) => {
    if (!html) return true;
    const cleaned = html
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/p>/gi, '')
      .replace(/<br\s*\/?>/gi, '')
      .replace(/&nbsp;/gi, '')
      .replace(/\s+/g, '')
      .trim();
    return cleaned.length === 0;
  };

  // Handle form submit with validation
  const handleSubmit = (e) => {
    e.preventDefault();

    // Notify parent that user attempted to save (for persisting validation state)
    if (onSaveAttempt) {
      onSaveAttempt();
    }

    // Define required fields for Event Details
    const requiredFields = [
      { field: 'therapeutic_area', label: 'Therapeutic Area' },
      { field: 'title', label: 'Quick Guide Title', isQuill: true },
      { field: 'event_location', label: 'Event Name & Location', isQuill: true },
      { field: 'disclaimers', label: 'Disclaimers', isQuill: true },
    ];

    const errors = {};
    for (const { field, isQuill } of requiredFields) {
      const value = guide[field];

      if (isQuill) {
        if (!value || isQuillEmpty(value)) {
          errors[field] = true;
        }
      } else {
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          errors[field] = true;
        }
      }
    }

    // Validate URL fields if they have values
    if (guide.society1_url && !isValidURL(guide.society1_url)) {
      errors.society1_url = true;
    }
    if (guide.society2_url && !isValidURL(guide.society2_url)) {
      errors.society2_url = true;
    }

    // If there are errors, set them and don't submit
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setUrlErrors({
        society1_url: errors.society1_url,
        society2_url: errors.society2_url
      });
      // Scroll to first error - include society-link-group for URL errors and select fields
      setTimeout(() => {
        const firstErrorField = document.querySelector('.guide-form .input-group.error, .guide-form .quill-wrapper.error, .guide-form .custom-select.error, .guide-form .header-backgrounds.error, .guide-form .society-link-group.error, .guide-form select.input-error');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      return;
    }

    // Clear errors and save
    setFieldErrors({});
    setUrlErrors({});
    onSave();
  };

  return (
    <div className={`guide-form ${isDisabled ? 'disabled' : ''}`}>
      {isDisabled && (
        <div className="published-notice">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM9 14V12H11V14H9ZM9 6H11V10H9V6Z" fill="currentColor"/>
          </svg>
          <span>This guide is published and cannot be edited. Duplicate it to make changes.</span>
        </div>
      )}

      <div className="form-required-notice">
        Required fields<span className="required-asterisk">*</span>
      </div>

      <div className="form-fields">
        {/* Therapeutic Area */}
        <div className={`input-group ${fieldErrors.therapeutic_area ? 'error' : ''}`}>
          <label htmlFor="therapeutic_area">
            Therapeutic Area<span className="required-asterisk">*</span>
            {fieldErrors.therapeutic_area && <ErrorIcon />}
          </label>
          <CustomDropdown
            id="therapeutic_area"
            value={guide.therapeutic_area || 'hematology'}
            options={therapeuticAreas}
            onChange={(e) => {
              const selectedArea = e.target.value;
              // Only update therapeutic_area - Builder.js handleGuideUpdate will
              // automatically reset header_option to prevent race conditions
              onUpdate('therapeutic_area', selectedArea);

              if (selectedArea) {
                clearFieldError('therapeutic_area');
              }
            }}
            onBlur={onBlur}
            disabled={isDisabled}
            hasError={fieldErrors.therapeutic_area}
          />
        </div>

        {/* Quick Guide Title */}
        <div className={`input-group ${fieldErrors.title ? 'error' : ''}`}>
          <label htmlFor="title">
            Quick Guide Title<span className="required-asterisk">*</span>
            {fieldErrors.title && <ErrorIcon />}
          </label>
          <div className={`quill-wrapper ${isDisabled ? 'disabled' : ''} ${fieldErrors.title ? 'error' : ''}`} id="title">
            <LimitedQuill
              value={guide.title || ''}
              onChange={(content) => {
                onUpdate('title', content);
                if (!isQuillEmpty(content)) clearFieldError('title');
              }}
              onBlur={onBlur}
              modules={quillModulesMinimal}
              formats={quillFormatsMinimal}
              placeholder="Enter title..."
              readOnly={isDisabled}
              maxLength={250}
            />
          </div>
          <span className="char-count">{getPlainTextLength(guide.title)}/250</span>
        </div>

        {/* Event Name & Location */}
        <div className={`input-group ${fieldErrors.event_location ? 'error' : ''}`}>
          <label htmlFor="event_location">
            Event Name & Location<span className="required-asterisk">*</span>
            {fieldErrors.event_location && <ErrorIcon />}
          </label>
          <div className={`quill-wrapper ${isDisabled ? 'disabled' : ''} ${fieldErrors.event_location ? 'error' : ''}`} id="event_location">
            <LimitedQuill
              value={guide.event_location || ''}
              onChange={(content) => {
                onUpdate('event_location', content);
                if (!isQuillEmpty(content)) clearFieldError('event_location');
              }}
              onBlur={onBlur}
              modules={quillModulesMinimal}
              formats={quillFormatsMinimal}
              placeholder="Enter event name and location..."
              readOnly={isDisabled}
              maxLength={250}
            />
          </div>
          <span className="char-count">{getPlainTextLength(guide.event_location)}/250</span>
        </div>

        {/* Header Background Image Selection */}
        <div className={`input-group ${fieldErrors.header_option ? 'error' : ''}`}>
          <label>
            Select Header Background Image
            {fieldErrors.header_option && <ErrorIcon />}
          </label>
          <div className={`header-backgrounds ${fieldErrors.header_option ? 'error' : ''}`}>
            {headerBackgrounds.map((bg) => (
              <div
                key={bg.id}
                className={`header-bg-option ${guide.header_option === bg.id ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                onClick={() => {
                  if (!isDisabled) {
                    onUpdate('header_option', bg.id);
                    clearFieldError('header_option');
                    // Trigger auto-save after update
                    if (onBlur) onBlur();
                  }
                }}
              >
                <img
                  className="header-bg-preview"
                  src={bg.image}
                  alt={bg.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
              />
              <div
                className="header-bg-fallback"
                style={{ display: 'none' }}
              >
                <span className="header-bg-logo">Genentech</span>
              </div>
                <span className="header-bg-name">{bg.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimers */}
        <div className={`input-group ${fieldErrors.disclaimers ? 'error' : ''}`}>
          <label htmlFor="disclaimers">
            Disclaimer(s)<span className="required-asterisk">*</span>
            {fieldErrors.disclaimers && <ErrorIcon />}
          </label>
          <div className={`quill-wrapper ${isDisabled ? 'disabled' : ''} ${fieldErrors.disclaimers ? 'error' : ''}`} id="disclaimers">
            <ReactQuill
              theme="snow"
              value={guide.disclaimers || ''}
              onChange={(content) => {
                onUpdate('disclaimers', content);
                if (!isQuillEmpty(content)) clearFieldError('disclaimers');
              }}
              onBlur={onBlur}
              modules={quillModulesMinimal}
              formats={quillFormatsMinimal}
              placeholder="Enter disclaimers..."
              readOnly={isDisabled}
            />
          </div>
        </div>

        {/* Include Medically Toggle */}
        <div className="input-group toggle-group">
          <label className="toggle-label">
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={guide.include_medically || false}
                onChange={(e) => {
                  onUpdate('include_medically', e.target.checked);
                  // Trigger auto-save with a slight delay for toggle changes
                  // This ensures state update completes before auto-save runs
                  if (onBlur) setTimeout(onBlur, 100);
                }}
                disabled={isDisabled}
              />
              <span className="toggle-slider"></span>
            </div>
            <span className="toggle-text">Include Medically</span>
          </label>
        </div>
        {/* Genentech Access Section */}
        <div className={`input-group ${fieldErrors.genentech_access_text ? 'error' : ''}`}>

          {guide.include_medically === true && (
            <div className="genentech-access-input">
              <div className={`quill-wrapper quill-wrapper-sm ${isDisabled ? 'disabled' : ''} ${fieldErrors.genentech_access_text ? 'error' : ''}`}>
                <ReactQuill
                  theme="snow"
                  value={guide.genentech_access_text !== undefined ? guide.genentech_access_text : 'Access Genentech sponsored presentations'}
                  onChange={(content) => {
                    onUpdate('genentech_access_text', content);
                    if (!isQuillEmpty(content)) clearFieldError('genentech_access_text');
                  }}
                  onBlur={onBlur}
                  modules={quillModulesMinimal}
                  formats={quillFormatsMinimal}
                  placeholder="Access Genentech sponsored presentations"
                  readOnly={isDisabled}
                />
              </div>
              {/* Medically Badge URL */}
              <div className={`medically-url-input ${urlErrors.medically_url ? 'error' : ''}`}>
                <label htmlFor="medically_url">
                  Medically Badge URL
                  {urlErrors.medically_url && <ErrorIcon />}
                </label>
                <input
                  type="url"
                  id="medically_url"
                  value={guide.medically_url || 'https://medically.gene.com/'}
                  onChange={(e) => {
                    onUpdate('medically_url', e.target.value);
                    if (isValidURL(e.target.value) || e.target.value === '') clearUrlError('medically_url');
                  }}
                  onBlur={(e) => handleUrlBlur('medically_url', e.target.value)}
                  placeholder="https://medically.gene.com"
                  disabled={isDisabled}
                  className={urlErrors.medically_url ? 'input-error' : ''}
                />
                {urlErrors.medically_url && (
                  <span className="error-message">Please enter a valid URL (e.g., https://example.com)</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Society Links Section */}
        <div className="input-group">
          <div className="section-header-with-toggle">
            <label className="toggle-label">
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={guide.show_society_links !== false}
                  onChange={(e) => onUpdate('show_society_links', e.target.checked)}
                  disabled={isDisabled}
                />
                <span className="toggle-slider"></span>
              </div>
              <span className="toggle-text">Society Links</span>
            </label>
            
          </div>

          {guide.show_society_links !== false && (
            <div className="society-links-box">
              {/* Dynamic Label Text */}
              <div className="society-link-group full-width">
                <label htmlFor="society_label_text">Section Label Text</label>
                <div className={`quill-wrapper quill-wrapper-sm ${isDisabled ? 'disabled' : ''}`}>
                  <ReactQuill
                    theme="snow"
                    value={guide.society_label_text !== undefined ? guide.society_label_text : ''}
                    onChange={(content) => onUpdate('society_label_text', content)}
                    onBlur={onBlur}
                    modules={quillModulesMinimal}
                    formats={quillFormatsMinimal}
                    placeholder="Click to visit these Societies"
                    readOnly={isDisabled}
                  />
                </div>
              </div>


              <div className="society-links-row">
                {/* Society 1 */}
                <div className="society-link-group">
                  <label htmlFor="society1_name">Society 1 Name</label>
                  <input
                    type="text"
                    id="society1_name"
                    value={guide.society1_name || ''}
                    onChange={(e) => onUpdate('society1_name', e.target.value)}
                    onBlur={onBlur}
                    placeholder="e.g., euretina.org"
                    disabled={isDisabled}
                  />
                </div>
                <div className={`society-link-group ${urlErrors.society1_url ? 'error' : ''}`}>
                  <label htmlFor="society1_url">
                    Society 1 URL
                    {urlErrors.society1_url && <ErrorIcon />}
                  </label>
                  <input
                    type="url"
                    id="society1_url"
                    value={guide.society1_url || ''}
                    onChange={(e) => {
                      onUpdate('society1_url', e.target.value);
                      if (isValidURL(e.target.value) || e.target.value === '') clearUrlError('society1_url');
                    }}
                    onBlur={(e) => handleUrlBlur('society1_url', e.target.value)}
                    placeholder="https://euretina.org"
                    disabled={isDisabled}
                    className={urlErrors.society1_url ? 'input-error' : ''}
                  />
                  {urlErrors.society1_url && (
                    <span className="error-message">Please enter a valid URL (e.g., https://example.com)</span>
                  )}
                </div>
              </div>

              <div className="society-links-row">
                {/* Society 2 */}
                <div className="society-link-group">
                  <label htmlFor="society2_name">Society 2 Name</label>
                  <input
                    type="text"
                    id="society2_name"
                    value={guide.society2_name || ''}
                    onChange={(e) => onUpdate('society2_name', e.target.value)}
                    onBlur={onBlur}
                    placeholder="e.g., retinasociety.org"
                    disabled={isDisabled}
                  />
                </div>
                <div className={`society-link-group ${urlErrors.society2_url ? 'error' : ''}`}>
                  <label htmlFor="society2_url">
                    Society 2 URL
                    {urlErrors.society2_url && <ErrorIcon />}
                  </label>
                  <input
                    type="url"
                    id="society2_url"
                    value={guide.society2_url || ''}
                    onChange={(e) => {
                      onUpdate('society2_url', e.target.value);
                      if (isValidURL(e.target.value) || e.target.value === '') clearUrlError('society2_url');
                    }}
                    onBlur={(e) => handleUrlBlur('society2_url', e.target.value)}
                    placeholder="https://retinasociety.org"
                    disabled={isDisabled}
                    className={urlErrors.society2_url ? 'input-error' : ''}
                  />
                  {urlErrors.society2_url && (
                    <span className="error-message">Please enter a valid URL (e.g., https://example.com)</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>



        {/* Project Code */}
        <div className={`input-group ${fieldErrors.project_code ? 'error' : ''}`}>
          <label htmlFor="project_code">
            Copyright Text / Project Code
            {fieldErrors.project_code && <ErrorIcon />}
          </label>
          <div className="input-wrapper">
            <input
              type="text"
              id="project_code"
              value={guide.project_code || ''}
              onChange={(e) => {
                onUpdate('project_code', e.target.value);
                if (e.target.value.trim()) clearFieldError('project_code');
              }}
              onBlur={onBlur}
              placeholder="© 2025 Genentech, Inc. All rights reserved. M-US-XXXXXXXX"
              maxLength={150}
              disabled={isDisabled}
            />
            <span className="char-count">{(guide.project_code || '').length}/150</span>
          </div>
        </div>


      </div>

      {!isDisabled && (
        <div className="form-actions">
          <button
            className="btn btn-primary btn-save"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className="spinner small"></span>
                Saving...
              </>
            ) : (
              'Save and Continue'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default GuideForm;
